import React from 'react';
import { Flex, Icon } from '@chakra-ui/react';
import { KText, KTag } from '@/ui-library/components';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { DrilldownMetricMappingHeader } from './constants';

export interface DrilldownContext {
  type: 'metrics' | 'chart' | 'table';
  metric?: string;
  dt?: string;
  yAxis?: string;
  xAxis?: string;
  g1?: any;
  g2?: any;
  g1Header?: any;
  g2Header?: any;
  tableHeader?: string;
  chartValue?: string; //optional
  metricValue?: any;
  deletedTag?: React.ReactNode;
  isG1Deleted?: boolean;
  isG2Deleted?: boolean;
}

export interface DrilldownBreadcrumbsProps {
  context: DrilldownContext;
}

export const getDrilldownContext = ({ context }) => {
  const {
    type,
    metric = '',
    yAxis = '',
    xAxis = '',
    g1Header,
    g2Header,
    tableHeader = '',
    chartValue = '',
    deletedTag = null,
    isG1Deleted = false,
    isG2Deleted = false,
  } = context;

  let metric_name = '';
  let dt_display_name = '';
  let g1_display_name = '';
  let g2_display_name = '';

  switch (type) {
    case 'metrics':
      metric_name = metric;
      break;

    case 'chart':
      dt_display_name = xAxis;
      g1_display_name = yAxis;
      if (chartValue) {
        g2_display_name = chartValue;
      }
      break;

    case 'table':
      const g1Title = g1Header || g1Header?.name || null;
      const g2Title = g2Header || g2Header?.name || null;
      metric_name = tableHeader || metric;
      g1_display_name = g1Title;
      g2_display_name = g2Title;
      break;

    default:
      return null;
  }
  return {
    metric_name,
    dt_display_name,
    g1_display_name,
    g2_display_name,
    deletedTag,
    isG1Deleted,
    isG2Deleted,
  };
};

export const DrilldownBreadcrumb: React.FC<DrilldownBreadcrumbsProps> = ({
  context,
}) => {
  const {
    type,
    metric = '',
    yAxis = '',
    xAxis = '',
    g1Header,
    g2Header,
    tableHeader = '',
    chartValue = '',
    metricValue = null,
    deletedTag = null,
    isG1Deleted = false,
    isG2Deleted = false,
  } = context;

  const renderBreadcrumbs = () => {
    let items = [];
    let deletedItemIndex = -1;

    switch (type) {
      case 'metrics':
        items = [metric];
        break;

      case 'chart':
        items = [yAxis, xAxis];
        if (chartValue) {
          // Check if chartValue matches any value in flowHealthDrilldownMetricMappingHeader
          const matchingKey = Object.keys(DrilldownMetricMappingHeader).find(
            (key) => DrilldownMetricMappingHeader[key] === chartValue
          );
          items.push(matchingKey || chartValue);
        }
        break;

      case 'table':
        const g1Title = g1Header || g1Header?.name || null;
        const g2Title = g2Header || g2Header?.name || null;
        items = [tableHeader || metric, g1Title, g2Title].filter(Boolean);

        // Determine which item should have the deleted tag
        if (isG1Deleted && g1Title) {
          // Find the index of g1Title in the items array
          deletedItemIndex = items.findIndex((item) => item === g1Title);
        } else if (isG2Deleted && g2Title) {
          // Find the index of g2Title in the items array
          deletedItemIndex = items.findIndex((item) => item === g2Title);
        }
        break;

      default:
        return null;
    }

    return (
      <Flex
        alignItems="center"
        justifyContent="start"
        gap="6px"
        width="100%"
        overflow="hidden"
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const shouldShowDeletedTag = deletedTag && index === deletedItemIndex;

          return (
            <Flex
              key={item}
              alignItems="center"
              flexShrink={1}
              minWidth={0}
              overflow="hidden"
              maxWidth={isLast ? '100%' : '50%'}
            >
              <KText
                size="md-semi-bold"
                textTransform="none"
                color="secondary-900"
                isTruncated={true}
                enableTooltip={true}
                // noOfLines={1}
              >
                {item}
              </KText>
              {shouldShowDeletedTag && (
                <Flex ml="8px" flexShrink={0}>
                  {deletedTag}
                </Flex>
              )}
              {!isLast && <Icon as={ChevronRight} flexShrink={0} ml="8px" />}
            </Flex>
          );
        })}
        <KTag value={metricValue} flexShrink={0} />
      </Flex>
    );
  };

  return renderBreadcrumbs();
};
